import { useState } from "react";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";
import { useAdminContent, AdminTitle } from "./shared";
import ImageField from "./ImageField";

export default function PagesEditor() {
  const { content, reload } = useAdminContent();
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(null);
  const [sectionsJson, setSectionsJson] = useState("");
  const [jsonError, setJsonError] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newTitle, setNewTitle] = useState("");

  if (!content) return <p className="text-clay">Loading…</p>;

  const pick = (slug) => {
    const doc = content.pages.find((p) => p.slug === slug);
    const payload = doc.staging || doc.live || { title: slug, hero_slides: [], sections: [] };
    setSelected(slug);
    setDraft({ ...payload });
    setSectionsJson(JSON.stringify(payload.sections || [], null, 2));
    setJsonError("");
  };

  const setSlide = (i, k, v) => {
    const slides = [...(draft.hero_slides || [])];
    slides[i] = { ...slides[i], [k]: v };
    setDraft({ ...draft, hero_slides: slides });
  };

  const save = async () => {
    let sections;
    try {
      sections = JSON.parse(sectionsJson || "[]");
      if (!Array.isArray(sections)) throw new Error();
    } catch {
      setJsonError("Sections JSON is invalid — fix it before saving.");
      return;
    }
    try {
      await api.put(`/admin/pages/${selected}`, { ...draft, sections });
      toast.success("Saved to staging — preview at /staging");
      reload();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const createPage = async () => {
    try {
      await api.post("/admin/pages", { slug: newSlug, title: newTitle });
      toast.success(`Page "${newSlug}" created in staging`);
      setNewSlug(""); setNewTitle("");
      reload();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail));
    }
  };

  const removePage = async (slug) => {
    if (!window.confirm(`Delete page "${slug}"? This removes it from staging AND live.`)) return;
    await api.delete(`/admin/pages/${slug}`);
    toast.success("Page deleted");
    setSelected(null);
    reload();
  };

  return (
    <div data-testid="pages-editor">
      <AdminTitle eyebrow="Content" title="Pages & Sliders">
        {selected && (
          <button onClick={save} data-testid="page-save-button" className="flex items-center gap-2 rounded-full bg-brass px-6 py-2.5 text-xs font-semibold uppercase tracking-widest text-cast hover:bg-flame hover:text-cream transition-colors">
            <Save size={14} /> Save to Staging
          </button>
        )}
      </AdminTitle>
      <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
        <div>
          <div className="rounded-sm border border-brass/10 bg-card">
            {content.pages.map((p) => (
              <button key={p.slug} onClick={() => pick(p.slug)} data-testid={`page-select-${p.slug}`}
                className={`flex w-full items-center justify-between px-5 py-3.5 text-sm transition-colors ${selected === p.slug ? "bg-brass/10 text-brass" : "text-clay hover:text-cream"}`}>
                <span>/{p.slug === "home" ? "" : p.slug}</span>
                {p.staging && JSON.stringify(p.staging) !== JSON.stringify(p.live) && <span className="h-2 w-2 rounded-full bg-flame" title="Unpublished changes" />}
              </button>
            ))}
          </div>
          <div className="mt-6 rounded-sm border border-brass/10 bg-card p-5">
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass mb-4">New Page</p>
            <input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="slug (e.g. rehearsal-dinners)" className="admin-input mb-3" data-testid="new-page-slug-input" />
            <input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Page title" className="admin-input mb-4" data-testid="new-page-title-input" />
            <button onClick={createPage} disabled={!newSlug} data-testid="new-page-create-button" className="flex items-center gap-2 rounded-full border border-brass/40 px-5 py-2 text-xs font-semibold uppercase tracking-widest text-brass hover:bg-brass/10 disabled:opacity-40">
              <Plus size={14} /> Add Page
            </button>
          </div>
        </div>
        <div>
          {!selected ? (
            <p className="text-clay text-sm" data-testid="page-editor-empty">Select a page to edit its title, hero slider, and sections.</p>
          ) : (
            <div className="space-y-8" data-testid="page-editor-form">
              <div className="rounded-sm border border-brass/10 bg-card p-6">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">Page Settings — /{selected === "home" ? "" : selected}</p>
                  {selected !== "home" && (
                    <button onClick={() => removePage(selected)} data-testid="page-delete-button" className="flex items-center gap-2 text-xs text-flame hover:underline"><Trash2 size={13} /> Delete page</button>
                  )}
                </div>
                <label className="admin-label">Title</label>
                <input value={draft.title || ""} onChange={(e) => setDraft({ ...draft, title: e.target.value })} className="admin-input mb-4" data-testid="page-title-input" />
                <label className="admin-label">SEO Description</label>
                <textarea rows={2} value={draft.seo_description || ""} onChange={(e) => setDraft({ ...draft, seo_description: e.target.value })} className="admin-input" data-testid="page-seo-input" />
              </div>
              <div className="rounded-sm border border-brass/10 bg-card p-6">
                <div className="flex items-center justify-between mb-5">
                  <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass">Hero Slider ({(draft.hero_slides || []).length} slides)</p>
                  <button onClick={() => setDraft({ ...draft, hero_slides: [...(draft.hero_slides || []), { title: "", subtitle: "", image: "", cta_label: "Book Now", cta_link: "/contact" }] })} data-testid="slide-add-button" className="flex items-center gap-2 text-xs text-brass hover:underline"><Plus size={13} /> Add slide</button>
                </div>
                <div className="space-y-6">
                  {(draft.hero_slides || []).map((slide, i) => (
                    <div key={i} className="rounded-sm border border-border p-5 space-y-3" data-testid={`slide-editor-${i}`}>
                      <div className="flex items-center justify-between">
                        <p className="text-xs font-mono text-clay">Slide {i + 1}</p>
                        <button onClick={() => setDraft({ ...draft, hero_slides: draft.hero_slides.filter((_, j) => j !== i) })} data-testid={`slide-remove-${i}`} className="text-xs text-flame hover:underline"><Trash2 size={13} /></button>
                      </div>
                      <input value={slide.title || ""} onChange={(e) => setSlide(i, "title", e.target.value)} placeholder="Headline" className="admin-input" data-testid={`slide-title-${i}`} />
                      <input value={slide.subtitle || ""} onChange={(e) => setSlide(i, "subtitle", e.target.value)} placeholder="Subtitle" className="admin-input" data-testid={`slide-subtitle-${i}`} />
                      <ImageField value={slide.image} onChange={(v) => setSlide(i, "image", v)} testid={`slide-image-${i}`} />
                      <div className="grid grid-cols-2 gap-3">
                        <input value={slide.cta_label || ""} onChange={(e) => setSlide(i, "cta_label", e.target.value)} placeholder="Button label" className="admin-input" data-testid={`slide-cta-label-${i}`} />
                        <input value={slide.cta_link || ""} onChange={(e) => setSlide(i, "cta_link", e.target.value)} placeholder="Button link (/contact)" className="admin-input" data-testid={`slide-cta-link-${i}`} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-sm border border-brass/10 bg-card p-6">
                <p className="text-xs font-mono uppercase tracking-[0.25em] text-brass mb-2">Sections (advanced JSON)</p>
                <p className="text-xs text-clay mb-4">Types: services, marquee, reviews, menu, blog_preview, blog_list, contact_form, richtext, cta, gallery, info</p>
                <textarea rows={14} value={sectionsJson} onChange={(e) => { setSectionsJson(e.target.value); setJsonError(""); }} className="admin-input font-mono text-xs" data-testid="sections-json-input" />
                {jsonError && <p className="mt-2 text-sm text-flame" data-testid="sections-json-error">{jsonError}</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
